import type { GwHistoryEntry, PlayerListItem } from '../types'

// Pure, client-side pick suggestion heuristic for the upcoming round.
// Replaceable later by a /v1/players/recommendations endpoint without touching callers.

export const PickReason = {
  Value:  'value',   // strong points-per-cost
  Form:   'form',    // trending up over recent rounds
  Points: 'points',  // high recent scoring
} as const
export type PickReason = (typeof PickReason)[keyof typeof PickReason]

export type SuggestedPick = {
  player:  PlayerListItem
  reasons: PickReason[]
  score:   number
}

const RECENT_WINDOW = 3
const VALUE_THRESHOLD = 0.4   // avgPoints per $ — e.g. a $10 player averaging 4+
const POINTS_THRESHOLD = 5    // recent average fantasy points

function recentHistory(player: PlayerListItem): GwHistoryEntry[] {
  return player.gwHistory.slice(-RECENT_WINDOW)
}

function formTrend(player: PlayerListItem): number {
  const recent = recentHistory(player)
  if (recent.length < 2) return 0
  const first = recent[0]?.points ?? 0
  const last  = recent[recent.length - 1]?.points ?? 0
  return last - first
}

function recentAvg(player: PlayerListItem): number {
  const recent = recentHistory(player)
  if (recent.length === 0) return player.avgPoints ?? 0
  const sum = recent.reduce((acc, entry) => acc + entry.points, 0)
  return sum / recent.length
}

function valueRatio(player: PlayerListItem): number {
  if (player.price <= 0) return 0
  return (player.avgPoints ?? 0) / player.price
}

export function suggestPicks(players: PlayerListItem[], limit = 3): SuggestedPick[] {
  const eligible = players.filter(p => !p.isEliminated && p.isAvailable)

  const scored: SuggestedPick[] = eligible.map(player => {
    const value = valueRatio(player)
    const trend = formTrend(player)
    const avg   = recentAvg(player)

    const score = value * 10 + Math.max(0, trend) * 1.5 + avg

    const reasons: PickReason[] = []
    if (value >= VALUE_THRESHOLD) reasons.push(PickReason.Value)
    if (trend > 0)                reasons.push(PickReason.Form)
    if (avg >= POINTS_THRESHOLD)  reasons.push(PickReason.Points)

    return { player, reasons, score }
  })

  return scored.sort((a, b) => b.score - a.score).slice(0, limit)
}
